import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Builder API
export const builderAPI = {
  list: () => axios.get(`${API}/builders`),
  get: (id) => axios.get(`${API}/builders/${id}`),
  create: (data) => axios.post(`${API}/builders`, data),
  update: (id, data) => axios.patch(`${API}/builders/${id}`, data),
};

// Project API
export const projectAPI = {
  list: () => axios.get(`${API}/projects`),
  get: (id) => axios.get(`${API}/projects/${id}`),
  create: (data) => axios.post(`${API}/projects`, data),
};

// Partner API
export const partnerAPI = {
  list: (status) => axios.get(`${API}/partners`, { params: { status } }),
  getMyProfile: () => axios.get(`${API}/partners/me`),
  approve: (id) => axios.post(`${API}/partners/${id}/approve`),
  assignProjects: (id, projectIds) => axios.post(`${API}/partners/${id}/assign-projects`, projectIds),
};

// Lead API
export const leadAPI = {
  list: (params) => axios.get(`${API}/leads`, { params }),
  create: (data) => axios.post(`${API}/leads`, data),
  update: (id, data) => axios.patch(`${API}/leads/${id}`, data),
  addFollowUp: (id, note, nextDate) => 
    axios.post(`${API}/leads/${id}/follow-up`, null, { params: { note, next_date: nextDate } }),
};

// Client Tagging API
export const taggingAPI = {
  list: (status) => axios.get(`${API}/tagging`, { params: { status } }),
  create: (data) => axios.post(`${API}/tagging`, data),
  approve: (id) => axios.post(`${API}/tagging/${id}/approve`),
  reject: (id, reason) => axios.post(`${API}/tagging/${id}/reject`, null, { params: { reason } }),
};

// Site Visit API
export const siteVisitAPI = {
  list: (params) => axios.get(`${API}/site-visits`, { params }),
  create: (data) => axios.post(`${API}/site-visits`, data),
  complete: (id, feedback) => axios.post(`${API}/site-visits/${id}/complete`, null, { params: { feedback } }),
};

// Commission API
export const commissionAPI = {
  list: (status) => axios.get(`${API}/commissions`, { params: { status } }),
  create: (data) => axios.post(`${API}/commissions`, data),
  pay: (id, reference) => axios.post(`${API}/commissions/${id}/pay`, null, { params: { payment_reference: reference } }),
};

// Inventory API
export const inventoryAPI = {
  list: (params) => axios.get(`${API}/inventory`, { params }),
  create: (data) => axios.post(`${API}/inventory`, data),
};

// Microsite API
export const micrositeAPI = {
  list: (status) => axios.get(`${API}/microsites`, { params: { status } }),
  get: (id) => axios.get(`${API}/microsites/${id}`),
  create: (data) => axios.post(`${API}/microsites`, data),
  update: (id, data) => axios.patch(`${API}/microsites/${id}`, data),
  submitForApproval: (id) => axios.post(`${API}/microsites/${id}/submit-for-approval`),
  approve: (id) => axios.post(`${API}/microsites/${id}/approve`),
};

// Collateral API
export const collateralAPI = {
  list: (params) => axios.get(`${API}/collaterals`, { params }),
  create: (data) => axios.post(`${API}/collaterals`, data),
  delete: (id) => axios.delete(`${API}/collaterals/${id}`),
  move: (id, folderId) => axios.put(`${API}/collaterals/${id}/move`, null, { params: { folder_id: folderId } }),
  getStats: () => axios.get(`${API}/collaterals/stats`),
  getForLead: (leadId) => axios.get(`${API}/collaterals/for-lead/${leadId}`),
  share: (leadId, collateralId, shareVia, message) => 
    axios.post(`${API}/collaterals/share`, null, { 
      params: { lead_id: leadId, collateral_id: collateralId, share_via: shareVia, message } 
    }),
  // Folder operations
  listFolders: () => axios.get(`${API}/collaterals/folders`),
  createFolder: (data) => axios.post(`${API}/collaterals/folders`, data),
  updateFolder: (id, data) => axios.put(`${API}/collaterals/folders/${id}`, data),
  deleteFolder: (id) => axios.delete(`${API}/collaterals/folders/${id}`),
};

// Sales Enablement API
export const salesEnablementAPI = {
  getForProject: (projectId) => axios.get(`${API}/sales-enablement/project/${projectId}`),
  createOrUpdate: (projectId, data) => axios.post(`${API}/sales-enablement/project/${projectId}`, data),
  search: (query) => axios.get(`${API}/sales-enablement/search`, { params: { q: query } }),
};

// Analytics API
export const analyticsAPI = {
  getPartnerPerformance: (period) => axios.get(`${API}/analytics/partner-performance`, { params: { period } }),
  getDashboard: () => axios.get(`${API}/analytics/dashboard`),
};

// Performance Goals API
export const goalsAPI = {
  list: (params) => axios.get(`${API}/goals`, { params }),
  create: (data) => axios.post(`${API}/goals`, data),
  getProgress: (period) => axios.get(`${API}/goals/progress`, { params: { period } }),
  getTierNotifications: () => axios.get(`${API}/goals/tier-notifications`),
  delete: (id) => axios.delete(`${API}/goals/${id}`),
};

// User API
export const userAPI = {
  list: (role) => axios.get(`${API}/users`, { params: { role } }),
  create: (data) => axios.post(`${API}/users`, data),
  toggleStatus: (id, isActive) => axios.patch(`${API}/users/${id}/status`, null, { params: { is_active: isActive } }),
  // Sub-user management
  createSubUser: (data) => axios.post(`${API}/users/sub-users`, data),
  listSubUsers: () => axios.get(`${API}/users/sub-users`),
  deleteSubUser: (id) => axios.delete(`${API}/users/sub-users/${id}`),
};

// AI Content API
export const aiAPI = {
  generateContent: (prompt, type) => 
    axios.post(`${API}/ai/generate-content`, null, { params: { prompt, content_type: type } }),
};

// CRM API
export const crmAPI = {
  // Lead activities
  getLeadActivities: (leadId) => axios.get(`${API}/crm/leads/${leadId}/activities`),
  createActivity: (leadId, data) => axios.post(`${API}/crm/leads/${leadId}/activities`, data),
  
  // Reminders
  getReminders: (params) => axios.get(`${API}/crm/reminders`, { params }),
  createReminder: (data) => axios.post(`${API}/crm/reminders`, data),
  completeReminder: (id) => axios.patch(`${API}/crm/reminders/${id}/complete`),
  dismissReminder: (id) => axios.patch(`${API}/crm/reminders/${id}/dismiss`),
  deleteReminder: (id) => axios.delete(`${API}/crm/reminders/${id}`),
  
  // Calendar
  getCalendarEvents: (params) => axios.get(`${API}/crm/calendar`, { params }),
  
  // Lead CRM details
  getLeadDetails: (leadId) => axios.get(`${API}/crm/lead/${leadId}/details`),
  updateDisposition: (leadId, status, notes) => 
    axios.patch(`${API}/crm/lead/${leadId}/disposition`, null, { params: { status, notes } }),
  
  // Enhanced CRM endpoints
  updateDispositionWithReminder: (leadId, data) =>
    axios.post(`${API}/crm/lead/${leadId}/disposition-with-reminder`, data),
  reassignLead: (leadId, newAssigneeId, notes) =>
    axios.post(`${API}/crm/lead/${leadId}/reassign`, null, { params: { new_assignee_id: newAssigneeId, notes } }),
  getContactHistory: (leadId) => axios.get(`${API}/crm/lead/${leadId}/contact-history`),
  getDispositionHistory: (leadId) => axios.get(`${API}/crm/lead/${leadId}/disposition-history`),
  getLeadSources: () => axios.get(`${API}/crm/lead-sources`),
  getTeamMembers: () => axios.get(`${API}/crm/team-members`),
  
  // Pipeline stages (Kanban)
  getPipelineStages: () => axios.get(`${API}/crm/pipeline-stages`),
  createPipelineStage: (data) => axios.post(`${API}/crm/pipeline-stages`, data),
  updatePipelineStage: (stageId, data) => axios.patch(`${API}/crm/pipeline-stages/${stageId}`, data),
  deletePipelineStage: (stageId) => axios.delete(`${API}/crm/pipeline-stages/${stageId}`),
  reorderPipelineStages: (stageOrders) => axios.post(`${API}/crm/pipeline-stages/reorder`, stageOrders),
  
  // Kanban view
  getLeadsKanban: () => axios.get(`${API}/crm/leads/kanban`),
  moveLeadStage: (leadId, newStage) => axios.patch(`${API}/crm/leads/${leadId}/move-stage`, null, { params: { new_stage: newStage } }),
  
  // Escalation
  getEscalationSettings: () => axios.get(`${API}/crm/escalation-settings`),
  updateEscalationSettings: (data) => axios.patch(`${API}/crm/escalation-settings`, data),
  getOverdueLeads: () => axios.get(`${API}/crm/leads/overdue`),
  checkEscalations: () => axios.post(`${API}/crm/leads/check-escalations`),
  getEscalationLogs: (params) => axios.get(`${API}/crm/escalation-logs`, { params }),
  resolveEscalation: (logId) => axios.patch(`${API}/crm/escalation-logs/${logId}/resolve`),
  
  // AI Features
  getLeadAIScore: (leadId) => axios.get(`${API}/crm/lead/${leadId}/ai-score`),
  generateAISuggestion: (leadId) => axios.post(`${API}/crm/lead/${leadId}/ai-suggestion`),
  askChatbot: (question, leadId) => axios.post(`${API}/crm/chatbot/ask`, null, { params: { question, lead_id: leadId } }),
  calculateAllScores: () => axios.post(`${API}/crm/leads/calculate-all-scores`),
};

// Notifications API
export const notificationsAPI = {
  list: (unreadOnly) => axios.get(`${API}/notifications`, { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => axios.get(`${API}/notifications/count`),
  markRead: (id) => axios.patch(`${API}/notifications/${id}/read`),
  markAllRead: () => axios.post(`${API}/notifications/mark-all-read`),
};

// Public Microsite API
export const publicAPI = {
  getMicrosite: (slug) => axios.get(`${API}/public/microsite/${slug}`),
  submitLead: (micrositeId, data) => 
    axios.post(`${API}/public/microsite/${micrositeId}/lead`, null, { params: data }),
};

// Payment Communication API (Mock effyVideo)
export const paymentAPI = {
  sendDemand: (leadId, amount, dueDate, description) =>
    axios.post(`${API}/effy-video/send-demand`, null, { 
      params: { lead_id: leadId, amount, due_date: dueDate, description } 
    }),
};

export default {
  builder: builderAPI,
  project: projectAPI,
  partner: partnerAPI,
  lead: leadAPI,
  tagging: taggingAPI,
  siteVisit: siteVisitAPI,
  commission: commissionAPI,
  inventory: inventoryAPI,
  microsite: micrositeAPI,
  collateral: collateralAPI,
  analytics: analyticsAPI,
  goals: goalsAPI,
  user: userAPI,
  ai: aiAPI,
  crm: crmAPI,
  notifications: notificationsAPI,
  public: publicAPI,
  payment: paymentAPI,
};
